import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

import {
	ReservationsChannelFilter,
	ReservationsDateFilter,
	ReservationsPropertyFilter,
	ReservationsStatusFilter,
} from "components/organisms";

import { ComplexFilterItem, ComplexFilterType } from "interfaces/filters";

import CityFilter from "./CityFilter";

interface Props {
	filter: ComplexFilterItem;
	onDelete: (type: ComplexFilterType) => void;
	onChange: (filter: ComplexFilterItem) => void;
}

const FilterTag = (props: Props): JSX.Element => {
	const [isDropdownVisible, setDropdownVisible] = useState(false);
	const [isInputActive, setInputActive] = useState(false);

	const { filter, onDelete, onChange } = props;

	useEffect(() => {
		if (filter.type === ComplexFilterType.CITY) {
			setInputActive(true);
		}
	}, [filter]);

	const getTitle = () => {
		switch (filter.type) {
			case ComplexFilterType.STATUS:
				return "Status:";
			case ComplexFilterType.CHANNEL:
				return "Channel:";
			case ComplexFilterType.PROPERTY_ID:
				return "Property:";
			case ComplexFilterType.DATE:
				return "Date:";
			case ComplexFilterType.CITY:
				return "City:";
			default:
				return "";
		}
	};

	const getFilterValue = () => {
		switch (filter.type) {
			case ComplexFilterType.STATUS:
				return filter.value;
			case ComplexFilterType.CHANNEL:
				return filter.value;
			case ComplexFilterType.PROPERTY_ID:
				return filter.value;
			case ComplexFilterType.CITY:
				return filter.value;
			case ComplexFilterType.DATE:
				if (filter.value) {
return `${dayjs(filter.value.dateFrom).format("LL")}-${dayjs(
						filter.value.dateTo
					).format("LL")}`;
				}

				return "";
			default:
				return "";
		}
	};

	const getFilterText = () => {
		const title = getTitle();

		const value = getFilterValue();

		if (value && filter.type !== ComplexFilterType.CITY) {
			return `${title} ${filter.title || value}`;
		}

		return title;
	};

	const filterText = getFilterText();

	const toggle = () => {
		if (filter.type === ComplexFilterType.CITY) {
			if (!isInputActive) {
				setInputActive(true);
			}
		} else {
			setDropdownVisible(true);
		}
	};

	const handleClose = () => {
		if (filter.type !== ComplexFilterType.CITY) {
			setDropdownVisible(false);
		} else {
			setInputActive(false);
		}
	};

	const renderInput = () => {
		switch (filter.type) {
			case ComplexFilterType.CITY:
				return (
					<CityFilter
						onClose={handleClose}
						onChange={onChange}
						value={filter.value || ""}
					/>
				);

			default:
				return <></>;
		}
	};

	const renderDropdown = () => {
		switch (filter.type) {
			case ComplexFilterType.STATUS:
				return (
					<ReservationsStatusFilter onClose={handleClose} onChange={onChange} />
				);
			case ComplexFilterType.CHANNEL:
				return (
					<ReservationsChannelFilter
						onClose={handleClose}
						onChange={onChange}
					/>
				);
			case ComplexFilterType.PROPERTY_ID:
				return (
					<ReservationsPropertyFilter
						onClose={handleClose}
						onChange={onChange}
					/>
				);
			case ComplexFilterType.DATE:
				return (
					<ReservationsDateFilter onClose={handleClose} onChange={onChange} />
				);
			default:
				return <></>;
		}
	};

	const renderDeleteButton = () => (
		<button
			onClick={() => onDelete(filter.type)}
			className="flex justify-center items-center cursor-pointer border-none outline-none bg-transparent ml-[5px] -mr-[5px] transition-all duration-200 hover:scale-110"
		>
			<X className="h-[15px] w-[15px] text-muted-foreground" />
		</button>
	);

	return (
		<div className="h-8 bg-secondary/10 border border-border rounded-md relative transition-all duration-300 hover:border-primary hover:shadow-[0_0_10px_rgba(0,217,255,0.2)]">
			<div
				onClick={toggle}
				className="cursor-pointer outline-none border-none flex items-center justify-center px-3 bg-transparent h-full w-full"
			>
				<p className="text-sm capitalize text-foreground">{`${filterText}`}</p>
				{isInputActive ? renderInput() : null}
				{renderDeleteButton()}
			</div>
			{isDropdownVisible ? renderDropdown() : null}
		</div>
	);
};

export default FilterTag;
